import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { BoxOverlay, boxesFromConfiguration } from '@/components/overlay/BoxOverlay'
import { FunnelLayout, type FunnelStep } from '@/layouts/FunnelLayout'
import { FxAlert, FxButton, FxIcon, FxPanel } from '@/components/fx'
import { ApiError, asLimitRefusal, documentsApi, templatesApi } from '@/lib/api'
import { pendingUpload } from '@/lib/pendingUpload'
import {
  DEFAULT_THRESHOLD,
  readDefaultThreshold,
} from '@/pages/einstellungen/defaultThreshold'
import { cn } from '@/lib/utils'
import {
  checklistStates,
  detectDetail,
  funnelProgress,
  liveSummary,
  limitCopy,
  sizeLabel,
  timedOut,
  type Progress,
  type StepKey,
  type StepState,
} from './analyseProgress'
import { templateDetecting, templateSettling } from '@/types/domain'
import type { DetectionResult, JobStatus, Template } from '@/types/domain'

/**
 * Step two of the funnel: the stashed file becomes a template with its fields
 * laid out, then the same file is read as a scan, and the result step gets a
 * document to show.
 *
 * ## Why the progress here is deliberately coarse
 *
 * The artboard draws a live field count and a 45 % bar. Neither has a data
 * source: `POST /v1/templates?detectMethod=…` is **one blocking request** that
 * answers when the detector is finished — no stream, no per-page callback, no
 * partial count. So the waiting state is an honest indeterminate stage
 * sequence, and the count-up and the box reveal happen *after* the response,
 * as a reveal of a known result. The bar only moves on numbers that exist:
 * the uploaded bytes, the completed stages, and the job's processed count.
 * A bar that invents its own percentage is a lie the person catches when it
 * sticks at 90 %.
 *
 * ## Why this screen runs four stages and not two
 *
 * The product's model is template-then-document, but the funnel reads a
 * *filled* form. So the same bytes go up twice: once to create the template
 * (which is the one detection path allowed to persist what it finds, because
 * there is no unsaved editor state to clobber), and once as a document to be
 * analysed. That is invisible to the person waiting, which is why all four
 * stages live on one screen.
 */

/** How long the reveal of an already-known result takes, end to end. */
const REVEAL_MS = 1600
/** One box's own fade-in, matching the `box-in` keyframe. */
const BOX_IN_MS = 320

/**
 * The gap between one box appearing and the next.
 *
 * Bounded at both ends: on a 40-field form an even division would put them
 * 40ms apart, which is below the eye's ability to see them as separate
 * arrivals and reads as a single flash; on a 3-field form it would leave
 * half a second between boxes, which reads as the thing being slow.
 */
function revealStaggerFor(total: number): number {
  return Math.min(90, Math.max(35, Math.round(REVEAL_MS / Math.max(total, 1))))
}
/** After this, the wait has outlasted the promise and says so. */
const SLOW_AFTER_MS = 45_000
/**
 * How long a PDF is given to become pages before this screen stops waiting.
 *
 * The converter records a failed conversion on the *job*, not on the
 * template, so a corrupt or zero-page PDF simply leaves `pageList` empty
 * forever. Without a deadline the settle poll runs every two seconds for as
 * long as the tab is open and nothing is ever said.
 */
const CONVERT_DEADLINE_MS = 5 * 60_000

/** This screen's four stages onto the wizard header's four steps. Step 1,
 *  "Hochladen", is the previous screen — everything here is step 2 onwards. */
const FUNNEL_STEP_OF: Record<StepKey, FunnelStep> = {
  transfer: 'detect',
  detect: 'detect',
  read: 'read',
  result: 'result',
}

/** A terminal branch: nothing more will happen without the person choosing. */
type Outcome =
  | { kind: 'empty' }
  // A plan refusal is not a failure — nothing is broken and retrying will not
  // help, so it gets its own answer rather than "noch einmal versuchen".
  | { kind: 'refused'; title: string; body: string }
  // The template exists and is saved; detection simply found nothing to bind.
  | { kind: 'noFields'; templateId: string; reason?: string }
  // We stopped waiting, but the gateway may well have finished. Offering a
  // retry here would risk a second template, so it offers a look instead.
  | { kind: 'timeout' }
  | { kind: 'failed'; message: string; templateId?: string }

export function AnalysePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<StepKey>('transfer')
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [file, setFile] = useState<{ name: string; size: number; isPdf: boolean } | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [transferPercent, setTransferPercent] = useState(0)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [detection, setDetection] = useState<DetectionResult | null>(null)
  const [fieldTotal, setFieldTotal] = useState<number | null>(null)
  const [revealed, setRevealed] = useState(0)
  const [revealDone, setRevealDone] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [slow, setSlow] = useState(false)
  const [convertGaveUp, setConvertGaveUp] = useState(false)
  // The uploaded scan's placeholder id, while a PDF is being rendered into
  // pages. Null for an image, whose id is the document itself.
  const [pendingPdfId, setPendingPdfId] = useState<string | null>(null)
  const [renderGaveUp, setRenderGaveUp] = useState(false)

  // The bytes are needed a second time, as a document, so the held file is
  // kept here and cleared from IndexedDB only once that second upload is
  // through — a failure in between must leave something to retry with.
  const held = useRef<File | null>(null)
  const started = useRef(false)
  const reading = useRef(false)
  // The analysis is started once, from whichever path got the real page ids
  // first — the upload's own answer, or the converter's pages.
  const queued = useRef(false)

  useEffect(() => {
    // The upload must not run twice: StrictMode double-invokes effects in
    // development, and two runs would create two templates.
    if (started.current) return
    started.current = true

    void (async () => {
      const waiting = await pendingUpload.take()
      if (!waiting) {
        setOutcome({ kind: 'empty' })
        return
      }

      held.current = waiting.file
      const isPdf = isPdfFile(waiting.file)
      setFile({ name: waiting.file.name, size: waiting.file.size, isPdf })
      // A PDF cannot be shown as an <img>; its preview is the rendered page
      // that arrives with the template, later.
      if (!isPdf) setPreviewUrl(URL.createObjectURL(waiting.file))

      // A reload (or a Back into this screen) finds the same file waiting —
      // `take()` does not consume it. Without resuming from what the run
      // already created, that second visit would create a second template
      // and a second document, both billed against the plan. The field
      // count is then read off the stored template rather than off an upload
      // response nobody is going to get again.
      const done = waiting.run
      if (done?.templateId) {
        setTransferPercent(100)
        setStep('detect')
        setTemplateId(done.templateId)
        setDetection({ method: waiting.detectMethod ?? 'cv', totalFields: 0, pending: true })
        if (done.documentId) {
          reading.current = true
          setDocumentId(done.documentId)
          if (done.jobId) {
            queued.current = true
            setJobId(done.jobId)
            setStep('read')
          } else if (isPdf) {
            // The scan was stored but never queued, and for a PDF the stored
            // id is the placeholder the converter deletes — so the resume
            // goes through the same wait for the rendered pages the first
            // visit does, rather than queueing a job on a row that is on its
            // way out.
            setPendingPdfId(done.documentId)
            setStep('read')
          } else {
            // The document was stored but the run never got queued. Starting
            // the analysis is idempotent enough — it is the same job the
            // first visit would have started — and re-uploading is not.
            try {
              queued.current = true
              const job = await documentsApi.startAnalysis(done.templateId, [done.documentId])
              void pendingUpload.markProgress({ jobId: job })
              setJobId(job)
              setStep('read')
            } catch (cause) {
              setOutcome({
                kind: 'failed',
                message: ApiError.from(cause).message,
                templateId: done.templateId,
              })
            }
          }
        }
        return
      }

      try {
        const result = await templatesApi.upload({
          file: waiting.file,
          filename: waiting.file.name,
          detectMethod: waiting.detectMethod ?? 'cv',
          // Consent was given on the upload screen; the gateway stamps the
          // template's own record from its configuration. Without it the
          // worker reads the page with OCR alone, which is the ordinary
          // result rather than the one the trial promises.
          aiConsent: waiting.aiConsent === true,
          onProgress: (sent, total) => {
            const percent = total > 0 ? Math.round((sent / total) * 100) : 0
            setTransferPercent(percent)
            // Once the bytes are across, the gateway is running the detector
            // — which is the slow part and has no progress to report.
            if (percent >= 100) setStep((current) => (current === 'transfer' ? 'detect' : current))
          },
        })
        setTransferPercent(100)
        setStep((current) => (current === 'transfer' ? 'detect' : current))
        setTemplateId(result.id)
        void pendingUpload.markProgress({ templateId: result.id })
        // The account's own default (Einstellungen → Auswertung) is a
        // browser-local preference the gateway has no column for, so it is
        // applied here, on the one path that creates a template. Without
        // this the setting says "gilt für Formulare, die Sie ab jetzt
        // anlegen" and changes nothing. A failure is not worth stopping the
        // funnel for: the template is created at the gateway's own 80 %, and
        // the form's own settings tab can still change it.
        const preferred = readDefaultThreshold()
        if (preferred !== DEFAULT_THRESHOLD) {
          try {
            await templatesApi.setConfidenceThreshold(result.id, preferred)
          } catch {
            /* keep the gateway's default rather than fail the upload */
          }
        }
        setDetection(result.detection ?? { method: waiting.detectMethod ?? 'cv', totalFields: 0 })
        // A template has been created, so any quota shown elsewhere is stale.
        void queryClient.invalidateQueries({ queryKey: ['usage'] })
      } catch (cause) {
        const error = ApiError.from(cause)
        const refusal = asLimitRefusal(error)
        if (refusal) {
          // The file stays put: the person may free up room and come back,
          // and re-picking it would be busywork.
          setOutcome({ kind: 'refused', ...limitCopy(refusal.code, refusal.used, refusal.limit) })
          return
        }
        setOutcome(timedOut(error) ? { kind: 'timeout' } : { kind: 'failed', message: error.message })
      }
    })()
  }, [queryClient])

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  // Both waiting stages can outlast the promise: detection is one long
  // blocking request, and the job may sit in the queue behind another batch.
  // Re-armed on every step change, so each stage gets its own clock.
  useEffect(() => {
    if ((step !== 'detect' && step !== 'read') || outcome) return
    setSlow(false)
    const timer = window.setTimeout(() => setSlow(true), SLOW_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [step, outcome])

  // The template is fetched for the stored page image and the saved boxes:
  // those coordinates are in the stored page's own frame, which is the frame
  // BoxOverlay draws in. A PDF's pages are rendered by the converter after
  // the upload answered, so this also polls until they exist.
  const template = useQuery({
    queryKey: ['template', templateId],
    queryFn: () => templatesApi.get(templateId as string),
    enabled: templateId != null,
    refetchInterval: (query) =>
      !convertGaveUp && templateSettling(query.state.data) ? 2000 : false,
  })

  // Detection runs in the converter for a PDF and an image alike, so the
  // upload answered `pending` and the template is "still settling" until its
  // pages are rendered and its status is out of `detecting`. Without
  // `pending` (a resume, or no detection asked for) an unresolved template
  // query is just a request in flight and must not be reported as this.
  const converting = detection?.pending === true && templateSettling(template.data)
  const stillDetecting = template.data != null && templateDetecting(template.data)

  // The deadline on that poll, and the terminal answer when it runs out.
  useEffect(() => {
    if (!converting || outcome || convertGaveUp) return
    const timer = window.setTimeout(() => setConvertGaveUp(true), CONVERT_DEADLINE_MS)
    return () => window.clearTimeout(timer)
  }, [converting, outcome, convertGaveUp])

  useEffect(() => {
    if (!convertGaveUp || outcome) return
    setOutcome({
      kind: 'failed',
      message: stillDetecting
        ? 'Die Felderkennung ist nicht fertig geworden. Das Formular ist angelegt — die Felder kannst du dort selbst einzeichnen.'
        : 'Dein PDF konnte nicht in einzelne Seiten umgewandelt werden. Das Formular ist angelegt — versuche es dort noch einmal oder lade die Seiten als Bild hoch.',
      templateId: templateId ?? undefined,
    })
  }, [convertGaveUp, outcome, templateId, stillDetecting])
  const page = template.data?.pageList?.[0]
  const boxes = useMemo(() => (page ? boxesFromConfiguration(page.boxConfiguration) : []), [page])

  // How many fields there are to reveal. When detection ran in the converter
  // (`pending`, now every upload that asks for it) it is whatever ended up
  // saved on the pages once the template has settled; an answer that came
  // with the upload itself is taken as it is.
  useEffect(() => {
    if (!detection || fieldTotal != null || converting) return
    if (detection.pending && !template.data) return
    const total = detection.pending
      ? (template.data?.pageList ?? []).reduce((sum, p) => sum + p.boxConfiguration.length, 0)
      : detection.totalFields
    setFieldTotal(total)
    if (total === 0) {
      setOutcome({ kind: 'noFields', templateId: templateId as string, reason: detection.error })
    }
  }, [detection, fieldTotal, converting, template.data, templateId])

  // The reveal. It animates a result that is already complete, so it is pure
  // decoration and is skipped entirely for anyone who asked for less motion.
  //
  // The boxes are all handed to the overlay at once and staggered there by
  // `animation-delay`; this effect only tracks when the last one has landed,
  // so the count beside it can stop climbing. It used to slice the array on a
  // 24ms interval — forty React renders inside a second, which made the box
  // reveal itself jitter and restarted the scan band's CSS transition on
  // every tick, so neither animation ran properly.
  useEffect(() => {
    if (fieldTotal == null || revealDone) return
    if (fieldTotal === 0 || prefersReducedMotion()) {
      setRevealed(fieldTotal)
      setRevealDone(true)
      return
    }
    const stagger = revealStaggerFor(fieldTotal)
    // The count climbs on its own clock, coarse enough not to re-render the
    // page every few milliseconds: it is a number ticking up, not the boxes.
    const countTimer = window.setInterval(() => {
      setRevealed((current) => Math.min(fieldTotal, current + Math.max(1, Math.round(fieldTotal / 12))))
    }, 80)
    const done = window.setTimeout(() => {
      window.clearInterval(countTimer)
      setRevealed(fieldTotal)
      setRevealDone(true)
    }, stagger * fieldTotal + BOX_IN_MS)
    return () => {
      window.clearInterval(countTimer)
      window.clearTimeout(done)
    }
  }, [fieldTotal, revealDone])

  // The template is the only source for a converting PDF's field count and
  // for the page image, so losing it is a dead end rather than a cosmetic
  // miss.
  useEffect(() => {
    if (!template.isError || outcome) return
    setOutcome({
      kind: 'failed',
      message: 'Dein Formular wurde angelegt, konnte aber nicht geladen werden.',
      templateId: templateId ?? undefined,
    })
  }, [template.isError, outcome, templateId])

  // Reading starts as soon as detection has landed — the reveal runs beside
  // it rather than delaying it, because 900 ms of animation is not a reason
  // to make someone wait longer.
  useEffect(() => {
    if (reading.current || outcome) return
    if (!templateId || fieldTotal == null || fieldTotal === 0 || !held.current) return
    reading.current = true

    void (async () => {
      try {
        const id = await documentsApi.upload(
          templateId,
          held.current as File,
          (held.current as File).name,
        )
        setDocumentId(id)
        // The file stays put until the run reaches its end: it is what a
        // reload resumes from, and clearing it here would leave a second
        // visit reporting "kein Formular ausgewählt" while the analysis it
        // is waiting for is still running.
        await pendingUpload.markProgress({ documentId: id })
        // The page is stored now, whatever happens next, so anything showing
        // the quota is stale from here on.
        void queryClient.invalidateQueries({ queryKey: ['usage'] })
        setStep('read')
        // A PDF's id is a placeholder: `convertDocument` writes one document
        // per rendered page and deletes it. An analysis started on it would
        // be a job on a row that no longer exists, and this screen would
        // then wait for a job that reads nothing. So the real ids are read
        // back from the converter's pages first, below.
        if (isPdfFile(held.current as File)) {
          setPendingPdfId(id)
          return
        }
        queued.current = true
        const job = await documentsApi.startAnalysis(templateId, [id])
        void pendingUpload.markProgress({ jobId: job })
        setJobId(job)
      } catch (cause) {
        const error = ApiError.from(cause)
        const refusal = asLimitRefusal(error)
        if (refusal) {
          setOutcome({ kind: 'refused', ...limitCopy(refusal.code, refusal.used, refusal.limit) })
          return
        }
        setOutcome({ kind: 'failed', message: error.message, templateId })
      }
    })()
  }, [templateId, fieldTotal, outcome, queryClient])

  // The converter's own pages for a PDF scan, looked up by the placeholder
  // id it was uploaded under. `GET …/documents?originalDocumentId=` answers
  // off `original_document_id`, so it keeps working after the placeholder row
  // is deleted — the id stays a usable handle on the upload, which is also
  // why the result screen is sent to it.
  const scanPages = useQuery({
    queryKey: ['document-pages', templateId, pendingPdfId],
    queryFn: () => documentsApi.pages(templateId as string, pendingPdfId as string),
    enabled: templateId != null && pendingPdfId != null && !renderGaveUp,
    refetchInterval: (query) => (convertedIds(query.state.data, pendingPdfId).length > 0 ? false : 2000),
  })

  useEffect(() => {
    const ids = convertedIds(scanPages.data, pendingPdfId)
    if (ids.length === 0 || queued.current || outcome || !templateId) return
    queued.current = true
    void (async () => {
      try {
        // Every rendered page in one job, so the screen has one status to
        // wait on and the person one result to look at.
        const job = await documentsApi.startAnalysis(templateId, ids)
        void pendingUpload.markProgress({ jobId: job })
        setJobId(job)
      } catch (cause) {
        setOutcome({ kind: 'failed', message: ApiError.from(cause).message, templateId })
      }
    })()
  }, [scanPages.data, pendingPdfId, outcome, templateId])

  // The same deadline the template conversion gets, for the same reason: a
  // PDF the converter cannot render leaves no pages and no marker anywhere a
  // client can read, so without a clock this poll runs for as long as the tab
  // is open and nothing is ever said.
  useEffect(() => {
    if (pendingPdfId == null || jobId || renderGaveUp || outcome) return
    const timer = window.setTimeout(() => setRenderGaveUp(true), CONVERT_DEADLINE_MS)
    return () => window.clearTimeout(timer)
  }, [pendingPdfId, jobId, renderGaveUp, outcome])

  useEffect(() => {
    if (!renderGaveUp || outcome) return
    setOutcome({
      kind: 'failed',
      message:
        'Dein ausgefülltes PDF konnte nicht in Seiten umgewandelt werden. Dein Formular ist angelegt — lade den Scan dort noch einmal hoch, am besten als Bild der Seite.',
      templateId: templateId ?? undefined,
    })
  }, [renderGaveUp, outcome, templateId])

  const job = useQuery<JobStatus>({
    queryKey: ['job', jobId],
    queryFn: () => documentsApi.jobStatus(jobId as string),
    enabled: jobId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'pending' || status === 'processing' ? 1500 : false
    },
  })

  // Losing the job query is a dead end of its own: the poll stops the moment
  // it errors (no data means no pending/processing status to poll on), so
  // without this the checklist spins on "Werte lesen" for ever. The scan is
  // stored either way, which is what the way out says.
  useEffect(() => {
    if (!job.isError || outcome || !templateId) return
    setOutcome({
      kind: 'failed',
      message:
        'Wir konnten den Stand der Auswertung nicht mehr abfragen. Dein Scan ist gespeichert — sieh im Formular nach, ob das Ergebnis inzwischen da ist.',
      templateId,
    })
  }, [job.isError, outcome, templateId])

  useEffect(() => {
    const status = job.data?.status
    if (!status || outcome || !templateId || !documentId) return
    if (status === 'completed') {
      setStep('result')
      // The run is over, so the held file has nothing left to resume.
      void pendingUpload.clear()
      // For a PDF this is the upload's placeholder id rather than an analysed
      // document's. That is the right id to hand on: the result screen asks
      // `…/documents?originalDocumentId=`, which answers with every rendered
      // page of the upload, and a single page id would show only one of them.
      navigate(`/onboarding/ergebnis/${templateId}/${documentId}`, { replace: true })
    } else if (status === 'failed' || status === 'cancelled') {
      setOutcome({
        kind: 'failed',
        // A cancelled job is somebody's decision, not a breakage — but from
        // here it is the same dead end and needs the same way out.
        message:
          status === 'cancelled'
            ? 'Die Auswertung wurde abgebrochen.'
            : (job.data?.error ?? 'Die Auswertung ist fehlgeschlagen.'),
        templateId,
      })
    }
  }, [job.data, outcome, templateId, documentId, navigate])

  const states = checklistStates(step, revealDone)
  const progress = funnelProgress({
    step,
    transferPercent,
    readPercent: job.data?.progress,
  })
  const failedPages = (detection?.pages ?? []).filter((p) => p.error)
  const goBack = useCallback(() => navigate('/onboarding'), [navigate])

  return (
    // The wizard header advances with the work: reading the values is its
    // own step in the header even though it happens on this screen.
    <FunnelLayout step={outcome ? 'detect' : FUNNEL_STEP_OF[step]}>
      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 sm:px-10 sm:py-10">
        {outcome ? (
          <OutcomePanel outcome={outcome} onRestart={goBack} />
        ) : (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
            {/* On a phone the panel is the screen — the preview is the thing
                you glance at, not the thing you read. */}
            <div className="order-2 flex flex-col gap-3 lg:order-1">
              <Preview
                previewUrl={previewUrl}
                page={page}
                boxes={boxes}
                appearStaggerMs={revealDone ? undefined : revealStaggerFor(boxes.length)}
                scanning={step === 'detect' && !revealDone}
                pdf={file?.isPdf ?? false}
              />
              <p className="text-small text-text-muted">
                {file ? `${file.name} · ${pageLabel(template.data, file.isPdf)}` : ' '}
              </p>
            </div>

            <FxPanel className="order-1 flex flex-col gap-6 lg:order-2">
              <div>
                <h1 className="text-tile-title">Wir lesen dein Formular</h1>
                <p className="mt-1 text-small text-text-muted">
                  Das dauert je nach Formular 10 bis 30 Sekunden.
                </p>
              </div>

              <ol className="flex flex-col gap-5">
                <ChecklistItem
                  state={states.transfer}
                  title="Datei übertragen"
                  detail={file ? `${pageLabel(template.data, file.isPdf)} · ${sizeLabel(file.size)}` : undefined}
                />
                <ChecklistItem
                  state={states.detect}
                  title="Felder werden erkannt"
                  detail={detectDetail(states.detect, fieldTotal, revealed, converting)}
                />
                {/* No "Seite N von M": `GET /v1/jobs/:jobId` answers a single
                    `progress` percentage and no page count, and the bar
                    already carries that number. */}
                <ChecklistItem state={states.read} title="Werte lesen" />
                <ChecklistItem state={states.result} title="Ergebnis" />
              </ol>

              <ProgressLine progress={progress} />

              {/* The screen changes on its own, so it has to say so out loud. */}
              <p className="text-small" role="status" aria-live="polite">
                {liveSummary(step, states, fieldTotal)}
              </p>

              {slow && (
                <FxAlert tone="info" title="Das dauert diesmal länger">
                  Eine Seite braucht auch mal eine Minute, und vor deinem Auftrag kann noch einer
                  laufen. Lass das Fenster offen — wir sind noch dran.
                </FxAlert>
              )}

              {failedPages.length > 0 && (
                <FxAlert tone="warning" title="Eine Seite konnten wir nicht lesen">
                  Auf {failedPages.map((p) => `Seite ${p.page}`).join(', ')} hat die Erkennung nicht
                  funktioniert. Die übrigen Seiten sind fertig — die fehlenden Felder kannst du
                  später selbst einzeichnen.
                </FxAlert>
              )}

              <p className="text-small text-text-muted">
                Du kannst das Fenster offen lassen – das Ergebnis erscheint hier von selbst.
              </p>
            </FxPanel>
          </div>
        )}
      </div>
    </FunnelLayout>
  )
}

// -- The waiting state -----------------------------------------------------

function Preview({
  previewUrl,
  page,
  boxes,
  appearStaggerMs,
  scanning,
  pdf,
}: {
  previewUrl: string | null
  page?: Template['pageList'][number]
  boxes: ReturnType<typeof boxesFromConfiguration>
  appearStaggerMs?: number
  scanning: boolean
  pdf: boolean
}) {
  // The stored page is the authority once it exists: its pixel frame is the
  // one the saved boxes are expressed in. Before then, the local file is a
  // perfectly good picture of what is being worked on, and it costs no
  // request.
  if (page && page.status === 'ready') {
    return (
      <BoxOverlay
        imagePath={page.imagePath}
        boxes={boxes}
        showLabels={false}
        appearStaggerMs={appearStaggerMs}
      />
    )
  }

  return (
    <div className="relative overflow-hidden rounded-sm bg-surface shadow-raised">
      {previewUrl ? (
        <img src={previewUrl} alt="" className="block w-full" />
      ) : (
        <div className="grid aspect-[2400/1692] place-items-center gap-2 text-text-muted">
          <FxIcon name={pdf ? 'picture_as_pdf' : 'description'} size={64} />
        </div>
      )}
      {scanning && <ScanBand />}
    </div>
  )
}

/**
 * The sweeping band from the artboard.
 *
 * It is a comfort animation, not a measurement — it says "still working" in
 * the place the person is looking. One state flip per sweep drives a CSS
 * transition, rather than a timer repainting on every frame.
 */
function ScanBand() {
  // Pure CSS. It used to be a React state toggle driving a 2200ms
  // `transition-[top]`, which meant every re-render of this subtree restarted
  // the transition mid-sweep — and the box reveal beside it re-rendered every
  // few milliseconds, so the band stuttered and jumped instead of sweeping.
  // A keyframe animation is owned by the browser and does not care how often
  // React renders.
  //
  // It also sweeps one way now. The old one eased down and then back up,
  // which reads as a pendulum; a scanner goes over the page.
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 h-[8%] animate-scan border-b-2 border-primary bg-gradient-to-b from-transparent via-primary/25 to-transparent motion-reduce:hidden"
    />
  )
}

function ChecklistItem({
  state,
  title,
  detail,
}: {
  state: StepState
  title: string
  detail?: string
}) {
  return (
    <li
      className="flex items-start gap-3"
      aria-current={state === 'running' ? 'step' : undefined}
    >
      <StepMarker state={state} />
      <span className={cn('block', state === 'pending' ? 'text-text-muted' : 'text-text')}>
        <span className="block text-body-strong">{title}</span>
        {/* The state must survive without the icon's colour. */}
        <span className="sr-only">{STATE_WORD[state]}</span>
        {detail && <span className="block text-small text-text-muted">{detail}</span>}
      </span>
    </li>
  )
}

const STATE_WORD: Record<StepState, string> = {
  done: '— erledigt',
  running: '— läuft gerade',
  pending: '— steht noch aus',
}

function StepMarker({ state }: { state: StepState }) {
  if (state === 'done') {
    return <FxIcon name="check_circle" size={24} className="text-success-text" />
  }
  if (state === 'running') {
    return <FxIcon name="progress_activity" size={24} className="animate-spin text-primary-text" />
  }
  return <FxIcon name="radio_button_unchecked" size={24} className="text-text-disabled" />
}

function ProgressLine({ progress }: { progress: Progress }) {
  return (
    <div
      className="h-1.5 overflow-hidden rounded-sm bg-fill"
      role="progressbar"
      aria-label="Fortschritt"
      aria-valuemin={0}
      aria-valuemax={100}
      // An indeterminate bar carries no `aria-valuenow`: that is exactly what
      // tells a screen reader the number is unknown rather than zero.
      aria-valuenow={progress.determinate ? Math.round(progress.value * 100) : undefined}
    >
      <div
        className={cn(
          'h-full rounded-sm bg-primary transition-[width] duration-500',
          !progress.determinate && 'animate-pulse',
        )}
        style={{ width: `${Math.round(progress.value * 100)}%` }}
      />
    </div>
  )
}

// -- The three dead ends, each with a way on ------------------------------

function OutcomePanel({ outcome, onRestart }: { outcome: Outcome; onRestart: () => void }) {
  const navigate = useNavigate()

  if (outcome.kind === 'empty') {
    return (
      <OutcomeCard title="Keine Datei gefunden">
        <p className="text-body text-text-muted">
          Deine Datei ist nicht mehr da — das passiert, wenn der Browser die Zwischenablage geleert
          hat. Bitte wähle sie noch einmal aus.
        </p>
        <FxButton className="mt-6" onClick={onRestart}>
          Formular auswählen
        </FxButton>
      </OutcomeCard>
    )
  }

  if (outcome.kind === 'refused') {
    return (
      <OutcomeCard title={outcome.title} accent="danger">
        <p className="text-body text-text-muted">{outcome.body}</p>
        {/* No "Guthaben kaufen" until there is a screen that sells it:
            `/billing` is not a route, so the button used to fall through the
            catch-all onto the overview — the same place the second button
            goes, while promising something else. */}
        <Ways>
          <FxButton onClick={() => navigate('/')}>Zu meinen Formularen</FxButton>
        </Ways>
      </OutcomeCard>
    )
  }

  if (outcome.kind === 'noFields') {
    return (
      <OutcomeCard title="Es wurden keine Felder gefunden" accent="warning">
        <p className="text-body text-text-muted">
          Dein Formular ist gespeichert — nur die Felder darin haben wir nicht erkannt. Das passiert
          bei Formularen ohne gedruckte Kästchen oder Linien. Zeichne die Felder einmal selbst ein,
          danach lesen wir jeden weiteren Scan davon automatisch.
        </p>
        {outcome.reason && <p className="mt-2 text-small text-text-muted">{outcome.reason}</p>}
        <Ways>
          <FxButton onClick={() => navigate(`/templates/${outcome.templateId}`)}>
            Felder einzeichnen
          </FxButton>
          <FxButton variant="tonal" onClick={onRestart}>
            Anderes Formular testen
          </FxButton>
        </Ways>
      </OutcomeCard>
    )
  }

  if (outcome.kind === 'timeout') {
    return (
      <OutcomeCard title="Das hat zu lange gedauert" accent="warning">
        <p className="text-body text-text-muted">
          Wir haben die Antwort nicht mehr abgewartet. Möglich, dass dein Formular trotzdem angelegt
          wurde — schau bitte zuerst in deine Formulare, bevor du es noch einmal hochlädst.
        </p>
        <Ways>
          <FxButton onClick={() => navigate('/')}>Zu meinen Formularen</FxButton>
          <FxButton variant="tonal" onClick={onRestart}>
            Noch einmal versuchen
          </FxButton>
        </Ways>
      </OutcomeCard>
    )
  }

  return (
    <OutcomeCard title="Das hat nicht funktioniert" accent="danger">
      <FxAlert tone="danger">{outcome.message}</FxAlert>
      <Ways>
        <FxButton onClick={onRestart}>Noch einmal versuchen</FxButton>
        {outcome.templateId && (
          <FxButton variant="tonal" onClick={() => navigate(`/templates/${outcome.templateId}`)}>
            Zum Formular
          </FxButton>
        )}
      </Ways>
    </OutcomeCard>
  )
}

/** Every dead end is the page's heading plus one panel — never a bare alert. */
function OutcomeCard({
  title,
  accent,
  children,
}: {
  title: string
  accent?: 'warning' | 'danger'
  children: React.ReactNode
}) {
  return (
    <div className="mx-auto w-full max-w-[640px]">
      <h1 className="text-page-title">{title}</h1>
      <FxPanel className="mt-6" accent={accent}>
        {children}
      </FxPanel>
    </div>
  )
}

/** A primary way on and an escape, stacked on a phone. */
function Ways({ children }: { children: React.ReactNode }) {
  return <div className="mt-6 flex flex-col gap-3 sm:flex-row">{children}</div>
}

/** "1 Seite" / "4 Seiten", and the honest shrug while a PDF is still rendering. */
function pageLabel(template: Template | undefined, isPdf: boolean): string {
  const count = template?.pageList?.length ?? template?.numPages ?? 0
  if (count > 0) return count === 1 ? '1 Seite' : `${count} Seiten`
  // A PDF's page count is unknown client-side until the converter has run.
  return isPdf ? 'PDF' : '1 Seite'
}

/**
 * The rendered pages of a PDF upload, without the upload itself.
 *
 * The lookup matches `original_document_id` **or** the row's own id, so while
 * the converter is still working it answers with the placeholder alone.
 * Reading that as "the pages are here" would queue the analysis on exactly
 * the row the converter is about to delete — the bug this wait exists to
 * avoid.
 */
export function convertedIds(
  docs: { id: string }[] | undefined,
  placeholderId: string | null,
): string[] {
  return (docs ?? []).filter((doc) => doc.id !== placeholderId).map((doc) => doc.id)
}

/**
 * Whether these bytes go through the converter.
 *
 * The extension is the gateway's own test (`isValidFileType`); the MIME type
 * is checked too because a file picked on a phone can arrive without one.
 */
function isPdfFile(file: File): boolean {
  return /\.pdf$/i.test(file.name) || file.type === 'application/pdf'
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}
